import * as React from "react";
import {Trans, useTranslation} from "react-i18next";
import InfoIcon from "../../../lib/InfoIcon/InfoIcon";
import {usePaymentContext} from "./Payment";
import {useRef} from "react";
import {pad} from "../Shipping/ShippingDatePicker";
import styles from "./PaymentBirthdayPicker.module.scss";

interface IPaymentBirthdayPickerProps {
  isScoringRequired: boolean;
  isSelectedPayment: boolean;
}

const liveYears = 120;

/**
 * Birthday picker for payment rows that require scoring - only visible when selected
 * @param props
 * @constructor
 */
const PaymentBirthdayPicker: React.FC<IPaymentBirthdayPickerProps> = (props: IPaymentBirthdayPickerProps) => {
  const {t} = useTranslation();
  const {dateOfBirthRef} = usePaymentContext();
  const dayRef = useRef<HTMLSelectElement>(null);
  const monthRef = useRef<HTMLSelectElement>(null);
  const yearRef = useRef<HTMLSelectElement>(null);

  const {isScoringRequired, isSelectedPayment} = props;

  if (!(isScoringRequired && isSelectedPayment)) {
    return null;
  }

  const earliestYear = new Date().getUTCFullYear() - liveYears;

  const checkDate = () => {
    if (
      dayRef.current === null ||
      dayRef.current.value === "Day" ||
      monthRef.current === null ||
      monthRef.current.value === "Month" ||
      yearRef.current === null ||
      yearRef.current.value === "Year"
    ) {
      return;
    }

    const [day, month, year] = [
      Number(dayRef.current.value),
      Number(monthRef.current.value),
      Number(yearRef.current.value),
    ];
    const generatedDay = new Date(year, month - 1, day).getDate();
    if (day !== generatedDay) {
      dayRef.current.value = (day - 1).toString();
      checkDate();

      return;
    }
    dateOfBirthRef.current = `${dayRef.current.value}.${monthRef.current.value}.${yearRef.current.value}`;
  };

  return (
    <div>
      <span>{t("form.birthday")}:</span>
      <div className={styles.wrapper}>
        <select ref={dayRef} name="Day" defaultValue={"Day"} onChange={checkDate}>
          <option key="DayHint" value="Day" disabled={true}>
            {t("form.day")}
          </option>
          {[...Array<number>(31)].map((value, index) => {
            return (
              <option key={index} value={pad(index + 1)}>
                {index + 1}
              </option>
            );
          })}
        </select>
        <select ref={monthRef} name="Month" defaultValue={"Month"} onChange={checkDate}>
          <option key="MonthHint" value="Month" disabled={true}>
            {t("form.month")}
          </option>
          {[
            t("form.january"),
            t("form.february"),
            t("form.march"),
            t("form.april"),
            t("form.may"),
            t("form.june"),
            t("form.july"),
            t("form.august"),
            t("form.september"),
            t("form.october"),
            t("form.november"),
            t("form.december"),
          ].map((value, index) => {
            return (
              <option key={`${value}+${index}`} value={pad(index + 1)}>
                {value}
              </option>
            );
          })}
        </select>
        <select ref={yearRef} name="Year" defaultValue={"Year"} onChange={checkDate}>
          <option key="YearHint" value="Year" disabled={true}>
            {t("form.year")}
          </option>
          {[...Array<number>(liveYears)].map((value, index) => {
            const reversedValue = earliestYear + liveYears - index;

            return (
              <option key={index} value={reversedValue}>
                {reversedValue}
              </option>
            );
          })}
        </select>
        <InfoIcon>
          <Trans
            i18nKey="checkout.birthdayNeeded"
            ns="translations"
            components={{
              a: <h3/>,
              p: <p/>
            }}
          />
        </InfoIcon>
      </div>
    </div>
  );
};

export default PaymentBirthdayPicker;
